/**
 * Employer-facing API client for the CI4/MySQL API: auth, own profile, own
 * jobs, applications to those jobs (admin-approved only) and the per-role
 * candidate access/$149 unlock. Builds the same EmployerStore shape the old
 * localStorage employer.ts module used, so employer.tsx/employer-preview.tsx's
 * existing rendering code needs minimal changes — only where the data comes
 * from. Covers both the self-serve ("direct", employerSignup) and
 * CLOSDD-sourced ("employerClaim", tied to an admin-created preview) account
 * paths — once claimed/signed up, both are ordinary employer sessions and
 * use every other function here identically.
 */
import type { Application, EmployerAccount, EmployerJob, EmployerStore, RoleAccess } from "@/components/site/employer";
import type { CandidateDirectoryEntry } from "@/components/site/showcase";

const API_URL = (import.meta.env["VITE_API_URL"] as string | undefined)?.replace(/\/$/, "") ?? "";

const TOKEN_KEY = "closdd.employer.token";

export function getEmployerToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setEmployerToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

/** Carries the HTTP status so callers can tell "token is actually invalid" (401) apart from a transient server/network hiccup. */
class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getEmployerToken();
  const res = await fetch(`${API_URL}/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(body?.messages?.error ?? body?.error ?? `Request failed (${res.status})`, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */

export type EmployerUser = { id: number; email: string; role: "employer" };

export async function employerLogin(email: string, password: string): Promise<EmployerUser> {
  const body = await authFetch<{ token: string; user: EmployerUser }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, role: "employer" }),
  });
  setEmployerToken(body.token);
  return body.user;
}

/** Posting a first role and creating the account happen together — see /post-job. */
export async function employerSignup(input: {
  company: string;
  contactName: string;
  email: string;
  phone: string;
  /** Country, "Name (code)" like the admin employer Location. */
  location?: string;
  website?: string;
  sector?: string;
}): Promise<{ user: EmployerUser; password: string }> {
  const body = await authFetch<{ token: string; password: string; user: EmployerUser }>("/auth/employer-signup", {
    method: "POST",
    body: JSON.stringify(input),
  });
  setEmployerToken(body.token);
  return { user: body.user, password: body.password };
}

/**
 * Claims the login for an employer record admin already created via a
 * CLOSDD-sourced preview (see fetchPublicPreview below) — used by
 * /employer-signup when it is reached with a ?ref=, instead of creating a
 * brand new employer record like employerSignup() does.
 */
export async function employerClaim(input: {
  ref: string;
  contactName: string;
  email: string;
  phone: string;
  password: string;
}): Promise<EmployerUser> {
  const body = await authFetch<{ token: string; user: EmployerUser }>("/auth/employer-claim", {
    method: "POST",
    body: JSON.stringify(input),
  });
  setEmployerToken(body.token);
  return body.user;
}

/**
 * Only a 401 means the token itself is actually invalid/expired — anything
 * else (a 500, a dropped connection) is a transient failure, and clearing a
 * still-good token on one of those was silently signing employers out of a
 * session that was otherwise fine.
 */
export async function getEmployerSession(): Promise<EmployerUser | null> {
  const token = getEmployerToken();
  if (!token) return null;
  try {
    const body = await authFetch<{ user: EmployerUser }>("/auth/me");
    return body.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      setEmployerToken(null);
    }
    return null;
  }
}

export async function employerLogout(): Promise<void> {
  const token = getEmployerToken();
  setEmployerToken(null);
  if (!token) return;
  try {
    await fetch(`${API_URL}/api/auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
  } catch {
    // token already cleared client-side
  }
}

export async function updateEmployerPassword(current: string, next: string): Promise<void> {
  await authFetch<void>("/employer/password", { method: "POST", body: JSON.stringify({ current, next }) });
}

/* ------------------------------------------------------------------ *
 * Workspace: profile + jobs + applications + access, combined into the
 * EmployerStore shape the existing dashboard renders.
 * ------------------------------------------------------------------ */

type ApiProfile = { company: string; contactName: string; email: string; phone: string; accountCreatedAt: string | null };
type ApiAccess = Record<string, RoleAccess>;

function toAccount(p: ApiProfile): EmployerAccount {
  return {
    company: p.company ?? "",
    contactName: p.contactName ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    // Login is by email now — these two are unused but kept so the shared
    // EmployerAccount type (still used by the old localStorage consoles)
    // is satisfied without ever holding a real password client-side.
    username: p.email ?? "",
    password: "",
    createdAt: p.accountCreatedAt ?? "",
  };
}

export async function fetchEmployerWorkspace(): Promise<EmployerStore> {
  const [profile, jobs, applications, access] = await Promise.all([
    authFetch<{ profile: ApiProfile }>("/employer/profile").then((b) => b.profile),
    authFetch<{ jobs: EmployerJob[] }>("/employer/jobs").then((b) => b.jobs),
    authFetch<{ applications: Application[] }>("/employer/applications").then((b) => b.applications),
    authFetch<{ access: ApiAccess }>("/employer/access").then((b) => b.access),
  ]);

  return {
    account: toAccount(profile),
    jobs,
    applications,
    loggedIn: true,
    plan: "free",
    sourced: false,
    invite: null,
    access,
  };
}

export async function updateEmployerProfile(patch: {
  company: string;
  contactName: string;
  email: string;
  phone: string;
}): Promise<EmployerAccount> {
  const body = await authFetch<{ profile: ApiProfile }>("/employer/profile", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return toAccount(body.profile);
}

/* ------------------------------------------------------------------ *
 * Jobs
 * ------------------------------------------------------------------ */

export type NewJobInput = Omit<EmployerJob, "id" | "status" | "createdAt" | "exclusive" | "decision" | "anonymous">;

export async function createEmployerJob(input: NewJobInput): Promise<EmployerJob> {
  const body = await authFetch<{ job: EmployerJob }>("/employer/jobs", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.job;
}

/** `resubmit` sends a rejected role back to Closdd for review (see Api\Employer\Jobs::update()). */
export async function updateEmployerJob(id: string, patch: Partial<EmployerJob> & { resubmit?: boolean }): Promise<EmployerJob> {
  const body = await authFetch<{ job: EmployerJob }>(`/employer/jobs/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return body.job;
}

export async function deleteEmployerJob(id: string): Promise<void> {
  await authFetch<void>(`/employer/jobs/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/* ------------------------------------------------------------------ *
 * Applications
 * ------------------------------------------------------------------ */

export async function updateApplicationStatus(id: string, status: Application["status"]): Promise<Application> {
  const body = await authFetch<{ application: Application }>(`/employer/applications/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
  return body.application;
}

/* ------------------------------------------------------------------ *
 * Candidate access / unlock
 * ------------------------------------------------------------------ */

/** Creates the Stripe PaymentIntent this role's checkout page confirms against. See Api\Employer\Access::intent(). */
export async function createUnlockIntent(jobId: string): Promise<{ clientSecret: string }> {
  return authFetch<{ clientSecret: string }>(
    `/employer/access/${encodeURIComponent(jobId)}/intent`,
    { method: "POST" },
  );
}

/**
 * Flips access on — only after the backend independently re-verifies the
 * given PaymentIntent with Stripe (status, job, amount). See
 * Api\Employer\Access::unlock().
 */
export async function unlockJobAccess(jobId: string, paymentIntentId: string): Promise<RoleAccess> {
  const body = await authFetch<{ access: RoleAccess }>(
    `/employer/access/${encodeURIComponent(jobId)}/unlock`,
    {
      method: "POST",
      body: JSON.stringify({ paymentIntentId }),
    },
  );
  return body.access;
}

export async function markApplicationViewed(jobId: string, applicationId: string): Promise<RoleAccess> {
  const body = await authFetch<{ access: RoleAccess }>(`/employer/access/${encodeURIComponent(jobId)}/viewed`, {
    method: "POST",
    body: JSON.stringify({ applicationId }),
  });
  return body.access;
}

/**
 * The "Access database" tab: every real candidate CLOSDD admin has granted
 * this employer access to (an empty array means no grant, or a paused one —
 * this is an admin-controlled allowlist, not self-serve). Replaces the old
 * hardcoded salesDatabase.
 */
export async function fetchEmployerCandidates(): Promise<CandidateDirectoryEntry[]> {
  const { candidates } = await authFetch<{ candidates: CandidateDirectoryEntry[] }>("/employer/candidates");
  return candidates;
}

/* ------------------------------------------------------------------ *
 * Public preview — the CLOSDD-sourced flow before an employer has an
 * account. No auth: used by /welcome/$ref and /roles/$ref.
 * ------------------------------------------------------------------ */

export type PublicPreviewCandidate = {
  id: string;
  firstName: string;
  /** First letter of their last name, e.g. "M" for "Jordan Morales" — null if they only have one name on file. */
  lastInitial: string | null;
  location: string;
  years: string;
  hasPitch: boolean;
  /** Real, playable pitch audio — the actual point of a preview. Null if none was recorded. */
  audioUrl: string | null;
  appliedAt: string;
};

export type PublicPreview = {
  ref: string;
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    setting: string;
    contract: string;
    pay: string;
    advert: string;
  };
  employer: { company: string; hasAccount: boolean };
  candidates: PublicPreviewCandidate[];
};

/** Returns null for an inactive/unknown ref rather than throwing — that is an expected, normal outcome here. */
export async function fetchPublicPreview(ref: string): Promise<PublicPreview | null> {
  const res = await fetch(`${API_URL}/api/public/previews/${encodeURIComponent(ref)}`);
  if (!res.ok) return null;
  const body = (await res.json()) as { preview: PublicPreview };
  return body.preview;
}

/**
 * Records that this employer invitation was actually opened — what the
 * admin console's "Employer journey" tracker needs to ever move past
 * "Invitation created". Fire-and-forget: a tracking call failing shouldn't
 * block the employer from seeing their candidates.
 */
export function markPreviewOpened(ref: string): void {
  void fetch(`${API_URL}/api/public/previews/${encodeURIComponent(ref)}/opened`, {
    method: "POST",
  });
}

/** Records that this candidate's pitch was played — feeds the "Candidates reviewed" stage. Fire-and-forget, same reasoning as markPreviewOpened. */
export function markCandidateListened(ref: string, candidateId: string): void {
  void fetch(`${API_URL}/api/public/previews/${encodeURIComponent(ref)}/listened`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ candidateId }),
  });
}
